/**
 * PersianJs v0.4.0
 * https://github.com/usablica/persian.js
 * MIT licensed
 *
 * Copyright (C) 2012 usabli.ca and other contributors
 */
    // Default config/variables
    const VERSION = "0.4.0";
    // Check for nodeJS
    const hasModule = (typeof module !== 'undefined' && module.exports);

    // Declare Number Arrays in different locales
    const arabicNumbers: string[] = ["١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩", "٠"],
        persianNumbers: string[] = ["۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹", "۰"],
        englishNumbers: string[] = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"];

    /**
     * PersianJs main class
     *
     * @class PersianJs
     */
    class PersianJs {
        private _str: string;

        constructor(str: string) {
            this._str = str;
        }

        /**
         * Used for convert Arabic characters to Persian
         *
         * @method _arabicChar
         * @param {String} value
         * @return {PersianJs} PersianJs Object
         */
        private _arabicChar(value: string): PersianJs {
            if (!value) {
                return this;
            }
            const arabicChars = ["ي", "ك", "‍", "دِ", "بِ", "زِ", "ذِ", "ِشِ", "ِسِ", "ى", "ة"];
            const persianChars = ["ی", "ک", "", "د", "ب", "ز", "ذ", "ش", "س", "ی", "ه"];

            for (let i = 0; i < arabicChars.length; i++) {
                value = value.replace(new RegExp(arabicChars[i], "g"), persianChars[i]);
            }
            this._str = value;
            return this;
        }

        /**
         * Used for convert Persian numbers to English
         * 
         * @method _persianNumber
         * @param {String} value
         * @return {PersianJs} PersianJs Object
         */
        private _persianNumber(value: string): PersianJs {
            if (!value) {
                return this;
            }

            for (let i = 0; i < englishNumbers.length; i++) {
                value = value.replace(new RegExp(persianNumbers[i], "g"), englishNumbers[i]);
            }

            this._str = value;
            return this;
        }

        /**
         * Used for convert Arabic numbers to Persian
         *
         * @method _arabicNumber
         * @param {String} value
         * @return {PersianJs} PersianJs Object
         */
        private _arabicNumber(value: string): PersianJs {
            if (!value) {
                return this;
            }
            value = value.toString();

            for (let i = 0; i < arabicNumbers.length; i++) {
                value = value.replace(new RegExp(arabicNumbers[i], "g"), persianNumbers[i]);
            }
            this._str = value;
            return this;
        }

        /**
         * Used for convert English numbers to Persian
         *
         * @method _englishNumber
         * @param {String} value
         * @return {PersianJs} PersianJs Object
         */
        private _englishNumber(value: string): PersianJs {
            if (!value) {
                return this;
            }
            value = value.toString();

            for (let i = 0; i < englishNumbers.length; i++) {
                value = value.replace(new RegExp(englishNumbers[i], "g"), persianNumbers[i]);
            }
            this._str = value;
            return this;
        }

        /**
         * Used for convert Persian and Arabic numbers to English string
         *
         * @method _toEnglishNumber
         * @param {String} value 
         * @return {PersianJs} PersianJs Object
         */
        private _toEnglishNumber(value: string): PersianJs {
            if (!value) {
                return this;
            }
            for (let i = 0; i < englishNumbers.length; i++) {
                value = value.replace(new RegExp(persianNumbers[i], "g"), englishNumbers[i])
                    .replace(new RegExp(arabicNumbers[i], "g"), englishNumbers[i]);
            }
            this._str = value;
            return this;
        }

        /**
         * Used for decode Persian Charachters in URL
         * https://fa.wikipedia.org/wiki/مدیاویکی:Gadget-Extra-Editbuttons-Functions.js
         *
         * @method _decodeURL
         * @param {String} value
         * @return {PersianJs} PersianJs Object
         */
        private _decodeURL(value: string): PersianJs {
            if (!value) {
                return this;
            }
            // Replace every %20 with _ to protect them from decodeURI
            let old = "";
            while (old !== value) {
                old = value;
                value = value.replace(/(http\S+?)\%20/g, '$1\u200c\u200c\u200c_\u200c\u200c\u200c');
            }
            // Decode URIs
            value = value.replace(/(http\S+)/g, function (s, p) {
                return decodeURI(p);
            });
            // Revive all instances of %20 to make sure no links are broken
            value = value.replace(/\u200c\u200c\u200c_\u200c\u200c\u200c/g, '%20');
            this._str = value;
            return this;
        }

        /**
         * Used for Change keyboard layout
         *
         * @method _switchKey
         * @param {String} value
         * @return {PersianJs} PersianJs Object
         */
        private _switchKey(value: string): PersianJs {
            if (!value) {
                return this;
            }
            const persianChar = ["ض", "ص", "ث", "ق", "ف", "غ", "ع", "ه", "خ", "ح", "ج", "چ", "ش", "س", "ی", "ب", "ل", "ا", "ت", "ن", "م", "ک", "گ", "ظ", "ط", "ز", "ر", "ذ", "د", "پ", "و", "؟", "ئ"];
            const englishChar = ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p", "[", "]", "a", "s", "d", "f", "g", "h", "j", "k", "l", ";", "'", "z", "x", "c", "v", "b", "n", "m", ",", "?", "m"];

            for (let i = 0; i < persianChar.length; i++) {
                value = value.replace(new RegExp(persianChar[i], "g"), englishChar[i]);
            }
            this._str = value;
            return this;
        }

        /**
         * Used for get persian words representation of a number
         *
         * @method _digitsToWords
         * @param {String} value
         * @return {PersianJs} PersianJs Object
         */
        private _digitsToWords(value: string): PersianJs {
            if (!isFinite(parseFloat(value))) {
                this._str = '';
                return this;
            }

            if (typeof value !== "string") {
                // @ts-ignore
                value = value.toString();
            }

            const parts = ['', 'هزار', 'میلیون', 'میلیارد', 'تریلیون', 'کوادریلیون', 'کویینتیلیون', 'سکستیلیون'];
            const numbers: { [key: string]: string[] } = {
                0: ['', 'صد', 'دویست', 'سیصد', 'چهارصد', 'پانصد', 'ششصد', 'هفتصد', 'هشتصد', 'نهصد'],
                1: ['', 'ده', 'بیست', 'سی', 'چهل', 'پنجاه', 'شصت', 'هفتاد', 'هشتاد', 'نود'],
                2: ['', 'یک', 'دو', 'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه'],
                two: ['ده', 'یازده', 'دوازده', 'سیزده', 'چهارده', 'پانزده', 'شانزده', 'هفده', 'هجده', 'نوزده'],
                zero: ['صفر']
            };
            const delimiter = ' و ';

            const valueParts = value.split('').reverse().join('')
                .replace(/\d{3}(?=\d)/g, "$&,")
                .split('').reverse().join('')
                .split(',').map(str => Array(4 - str.length).join('0') + str);

            const result = valueParts.map((three, iThree) => {
                const res = numbers[0][+three[0]] + delimiter +
                    (+three[1] === 1 ? numbers.two[+three[2]] : numbers[1][+three[1]] + delimiter + numbers[2][+three[2]]);
                return (res && res !== delimiter) ? res + ' ' + parts[iThree] + delimiter : '';
            }).reverse().join('').trim().slice(0, -1 * delimiter.length);

            this._str = result || numbers.zero[0];
            return this;
        }

        // Public Methods

        /**
         * Get the version
         *
         * @method version
         * @return {String} PersianJs version
         */
        version(): string {
            return VERSION;
        }

        /**
         * Convert Arabic characters to Persian
         *
         * @method arabicChar
         * @return {PersianJs} PersianJs Object
         */
        arabicChar(): PersianJs {
            return this._arabicChar(this._str);
        }

        /**
         * Convert Persian numbers to English
         *
         * @method persianNumber
         * @return {PersianJs} PersianJs Object
         */
        persianNumber(): PersianJs {
            return this._persianNumber(this._str);
        }

        /**
         * Convert Arabic numbers to Persian
         *
         * @method arabicNumber
         * @return {PersianJs} PersianJs Object
         */
        arabicNumber(): PersianJs {
            return this._arabicNumber(this._str);
        }

        /**
         * Convert English numbers to Persian
         *
         * @method englishNumber
         * @return {PersianJs} PersianJs Object
         */
        englishNumber(): PersianJs {
            return this._englishNumber(this._str);
        }

        /**
         * Convert Persian and Arabic numbers to English string
         *
         * @method toEnglishNumber
         * @return {PersianJs} PersianJs Object
         */
        toEnglishNumber(): PersianJs {
            return this._toEnglishNumber(this._str);
        }

        /**
         * Decode Persian Charachters in URL
         *
         * @method decodeURL
         * @return {PersianJs} PersianJs Object
         */
        decodeURL(): PersianJs {
            return this._decodeURL(this._str);
        }

        /**
         * Change keyboard layout
         *
         * @method switchKey
         * @return {PersianJs} PersianJs Object
         */
        switchKey(): PersianJs {
            return this._switchKey(this._str);
        }

        /**
         * Convert persian numbers to words
         *
         * @method digitsToWords
         * @return {PersianJs} PersianJs Object
         */
        digitsToWords(): PersianJs {
            return this._digitsToWords(this._str);
        }

        /**
         * Returns the manipulated string
         *
         * @method toString
         * @return {String} Final manipulated string
         */
        toString(): string {
            return this._str;
        }

        /**
         * Return PersianJs Object
         *
         * @method value
         * @return {String} Final manipulated string
         */
        value(): string {
            return this._str;
        }
    }

    /**
     * Create PersianJs Object
     *
     * @method PersianJs
     * @param {String} str
     * @return {PersianJs} PersianJs Object
     */
    const persianJs = function (str: string) {
        return new PersianJs(str);
    }

    // Export it to global
    export default persianJs
